/**
 * Plays inaudible audio while recording so the browser treats the tab as
 * "actively playing audio" — gives Android Chrome a longer background
 * tolerance window. Harmless on iOS / unsupported browsers.
 */

type SilentAudioHandle = {
  ctx: AudioContext
  oscillator: OscillatorNode
  gain: GainNode
}

let handle: SilentAudioHandle | null = null

function getAudioContextCtor(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  const w = window as Window & {
    AudioContext?: typeof AudioContext
    webkitAudioContext?: typeof AudioContext
  }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

export async function startSilentAudio(): Promise<void> {
  if (handle) return

  const Ctor = getAudioContextCtor()
  if (!Ctor) return

  try {
    const ctx = new Ctor()
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume()
      } catch {
        // ignore — autoplay policy may require a later gesture
      }
    }
    const gain = ctx.createGain()
    gain.gain.value = 0
    const oscillator = ctx.createOscillator()
    oscillator.frequency.value = 1
    oscillator.connect(gain).connect(ctx.destination)
    oscillator.start()
    handle = { ctx, oscillator, gain }
  } catch (err) {
    console.warn('[silentAudio] start failed', err)
  }
}

export async function stopSilentAudio(): Promise<void> {
  const current = handle
  handle = null
  if (!current) return
  try {
    current.oscillator.stop()
    current.oscillator.disconnect()
    current.gain.disconnect()
    await current.ctx.close()
  } catch (err) {
    console.warn('[silentAudio] stop failed', err)
  }
}

export function isSilentAudioRunning(): boolean {
  return handle !== null
}
